<script>
  import { _ } from '@sveltia/i18n';
  import { Button, Icon, TextArea } from '@sveltia/ui';
  import { onDestroy, onMount } from 'svelte';

  import {
    aiChatBusy,
    aiChatMessages,
    aiChatSessionUrl,
    aiChatTaskId,
    appendAiChatMessage,
    loadStoredTaskId,
    resetAiChatSession,
    showAiChatDrawer,
    storeTaskId,
  } from '$lib/services/integrations/gitbase/ai-chat';
  import {
    createAgentTask,
    formatAgentError,
    getAgentBranchName,
    getAgentStateLabel,
    getAgentTask,
    isAgentTaskActive,
    steerAgentTask,
  } from '$lib/services/integrations/gitbase/github-agent';
  import { openNewTab } from '$lib/services/utils/window';

  let draft = $state('');
  /** @type {ReturnType<typeof setInterval> | undefined} */
  let pollTimer;
  /** @type {string | undefined} */
  let lastState;
  /** @type {string | undefined} */
  let lastBranch;
  let completedAnnounced = $state(false);

  const closeDrawer = () => {
    showAiChatDrawer.set(false);
  };

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }
  };

  /** @param {string} taskId */
  const startPolling = (taskId) => {
    stopPolling();

    pollTimer = setInterval(async () => {
      try {
        await refreshTask(taskId);
      } catch (error) {
        appendAiChatMessage('error', formatAgentError(/** @type {Error} */ (error)));
        aiChatBusy.set(false);
        stopPolling();
      }
    }, 2500);
  };

  /** @param {string} taskId */
  const refreshTask = async (taskId) => {
    const task = await getAgentTask(taskId);

    aiChatTaskId.set(task.id);

    if (task.html_url) {
      aiChatSessionUrl.set(task.html_url);
    }

    if (task.state !== lastState) {
      lastState = task.state;
      appendAiChatMessage('status', getAgentStateLabel(task.state));
    }

    const branch = getAgentBranchName(task);

    if (branch && branch !== lastBranch) {
      lastBranch = branch;
      appendAiChatMessage('status', _('agent_branch_x', { values: { branch } }));
    }

    if (!isAgentTaskActive(task.state)) {
      aiChatBusy.set(false);
      stopPolling();

      if (task.state === 'completed' && !completedAnnounced) {
        completedAnnounced = true;
        appendAiChatMessage(
          'agent',
          _('agent_completed_message'),
          task.html_url,
        );
      }
    }
  };

  const sendMessage = async () => {
    const prompt = draft.trim();

    if (!prompt || $aiChatBusy) {
      return;
    }

    draft = '';
    appendAiChatMessage('user', prompt);
    aiChatBusy.set(true);

    try {
      const existingTaskId = $aiChatTaskId ?? loadStoredTaskId();

      if (existingTaskId && lastState === 'waiting_for_user') {
        try {
          await steerAgentTask(existingTaskId, prompt);
          storeTaskId(existingTaskId);
          aiChatTaskId.set(existingTaskId);
          lastState = 'in_progress';
          appendAiChatMessage('status', getAgentStateLabel('in_progress'));
          startPolling(existingTaskId);
          return;
        } catch {
          appendAiChatMessage('status', _('agent_steer_fallback'));
        }
      }

      const task = await createAgentTask({ prompt });

      storeTaskId(task.id);
      aiChatTaskId.set(task.id);
      lastState = task.state;
      lastBranch = undefined;
      completedAnnounced = false;

      if (task.html_url) {
        aiChatSessionUrl.set(task.html_url);
      }

      appendAiChatMessage('status', getAgentStateLabel(task.state));
      startPolling(task.id);
    } catch (error) {
      aiChatBusy.set(false);
      appendAiChatMessage('error', formatAgentError(/** @type {Error} */ (error)));
    }
  };

  onMount(async () => {
    const storedTaskId = loadStoredTaskId();

    if (!storedTaskId) {
      return;
    }

    aiChatTaskId.set(storedTaskId);
    aiChatBusy.set(true);

    try {
      await refreshTask(storedTaskId);

      if (
        lastState &&
        isAgentTaskActive(
          /** @type {import('$lib/services/integrations/gitbase/github-agent').AgentTaskState} */ (
            lastState
          ),
        )
      ) {
        startPolling(storedTaskId);
      } else {
        aiChatBusy.set(false);
      }
    } catch {
      storeTaskId(undefined);
      aiChatBusy.set(false);
    }
  });

  onDestroy(() => {
    stopPolling();
  });
</script>

{#if $showAiChatDrawer}
  <div role="none" class="backdrop" onclick={closeDrawer}></div>
  <aside class="drawer" aria-label={_('ai_assistant')}>
    <header role="none" class="header">
      <div role="none" class="title">
        <Icon name="smart_toy" />
        <h2>{_('ai_assistant')}</h2>
      </div>
      <div role="none" class="actions">
        {#if $aiChatSessionUrl}
          <Button
            variant="ghost"
            iconic
            aria-label={_('open_agent_session')}
            onclick={() => {
              openNewTab($aiChatSessionUrl);
            }}
          >
            {#snippet startIcon()}
              <Icon name="open_in_new" />
            {/snippet}
          </Button>
        {/if}
        <Button
          variant="ghost"
          iconic
          aria-label={_('new_ai_chat')}
          onclick={() => {
            stopPolling();
            resetAiChatSession();
            lastState = undefined;
            lastBranch = undefined;
            completedAnnounced = false;
            aiChatBusy.set(false);
          }}
        >
          {#snippet startIcon()}
            <Icon name="add_comment" />
          {/snippet}
        </Button>
        <Button variant="ghost" iconic aria-label={_('close')} onclick={closeDrawer}>
          {#snippet startIcon()}
            <Icon name="close" />
          {/snippet}
        </Button>
      </div>
    </header>

    <div role="log" aria-live="polite" aria-relevant="additions" class="messages">
      {#if !$aiChatMessages.length}
        <p class="hint">{_('ai_assistant_hint')}</p>
      {/if}

      {#each $aiChatMessages as message (message.id)}
        <article class={message.role} data-role={message.role}>
          {#if message.href}
            <p>
              {message.text}
              <a href={message.href} target="_blank" rel="noopener noreferrer">{_('open_agent_session')}</a>
            </p>
          {:else}
            <p>{message.text}</p>
          {/if}
        </article>
      {/each}
    </div>

    <footer role="none" class="composer">
      <TextArea
        bind:value={draft}
        flex
        rows={3}
        placeholder={_('ai_assistant_placeholder')}
        onkeydown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            sendMessage();
          }
        }}
      />
      <Button variant="primary" disabled={$aiChatBusy || !draft.trim()} onclick={sendMessage}>
        {$aiChatBusy ? _('ai_assistant_sending') : _('ai_assistant_send')}
      </Button>
    </footer>
  </aside>
{/if}

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 100;
    background-color: rgb(0 0 0 / 35%);
  }

  .drawer {
    position: fixed;
    inset: 0 0 0 auto;
    z-index: 101;
    display: flex;
    flex-direction: column;
    width: min(420px, 100vw);
    background-color: var(--sui-secondary-background-color);
    border-left: 1px solid var(--sui-secondary-border-color);
    box-shadow: var(--sui-popup-shadow);
  }

  .header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 8px 8px 16px;
    border-bottom: 1px solid var(--sui-secondary-border-color);
  }

  .title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    h2 {
      margin: 0;
      font-size: var(--sui-font-size-large);
      font-weight: 600;
    }
  }

  .actions {
    display: flex;
    margin-left: auto;
  }

  .messages {
    flex: auto;
    overflow-y: auto;
    padding: 16px;
  }

  .hint {
    margin: 0;
    color: var(--sui-secondary-foreground-color);
    line-height: 1.5;
  }

  article {
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    line-height: 1.5;

    p {
      margin: 0;
      white-space: pre-wrap;
    }

    &[data-role='user'] {
      margin-left: 24px;
      background-color: var(--sui-primary-background-color);
    }

    &[data-role='agent'] {
      margin-right: 24px;
      background-color: var(--sui-tertiary-background-color);
    }

    &[data-role='status'] {
      color: var(--sui-secondary-foreground-color);
      font-size: var(--sui-font-size-small);
      text-align: center;
      background: transparent;
      padding: 0;
    }

    &[data-role='error'] {
      background-color: var(--sui-error-background-color, #fdecea);
      color: var(--sui-error-foreground-color, #611a15);
    }

    a {
      display: inline-block;
      margin-top: 8px;
    }
  }

  .composer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px 16px;
    border-top: 1px solid var(--sui-secondary-border-color);
  }
</style>
